import React from "react";
import * as Sentry from "@sentry/react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  /** When true shows a compact "page crashed" card instead of a full-screen overlay.
   *  Use for per-route boundaries so the outer shell stays intact. */
  inline?: boolean;
}

const defaultFallback = (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      gap: "1rem",
    }}
  >
    <p>Something went wrong</p>
    <button onClick={() => window.location.reload()}>Reload page</button>
  </div>
);

/** Compact fallback shown for route-level boundaries — keeps the URL bar usable */
const inlineFallback = (
  <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-4 text-center">
    <p className="text-lg font-semibold text-foreground">This page crashed</p>
    <p className="text-sm text-muted-foreground max-w-xs">
      An unexpected error occurred. Your data is safe — try reloading or go back to the dashboard.
    </p>
    <div className="flex gap-3">
      <button
        className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        onClick={() => window.location.reload()}
      >
        Reload
      </button>
      <a
        href="/"
        className="px-4 py-2 text-sm rounded-md border border-border hover:bg-accent transition-colors"
      >
        Dashboard
      </a>
    </div>
  </div>
);

export function ErrorBoundary({ children, fallback, inline }: ErrorBoundaryProps) {
  const resolved = fallback ?? (inline ? inlineFallback : defaultFallback);
  return (
    <Sentry.ErrorBoundary fallback={() => <>{resolved}</>}>
      {children}
    </Sentry.ErrorBoundary>
  );
}
